import * as React from 'react';

import {UseFormRegisterReturn} from 'react-hook-form';
import {BasicTheme} from 'src/utils/makeTailwindBasicThemeClasses';

import {ErrorTextBlurb} from './formUtils/errorTextBlurb';

type FieldTypesThatBecomeInputElements =
  | 'checkbox'
  | 'dateInput'
  | 'emailInput'
  | 'phoneNumberInput'
  | 'radiobuttonGroup'
  | 'textInput'
  | 'urlInput';

type BaseLeadFormFieldDescriptor = {
  _key: string;
  fieldLabel: string;
  salesforceFieldName: string;
  isRequired?: boolean;
};

type DropdownField = {
  _type: 'dropdown';
  multiselectOptions: string[];
  placeholder?: string;
} & BaseLeadFormFieldDescriptor;

type EmailInputField = {
  _type: 'emailInput';
} & BaseLeadFormFieldDescriptor;

type TextareaField = {
  _type: 'textarea';
} & BaseLeadFormFieldDescriptor;

type OtherFields = {
  _type: FieldTypesThatBecomeInputElements;
} & BaseLeadFormFieldDescriptor;

export type LeadFormFieldDescriptor = DropdownField | EmailInputField | TextareaField | OtherFields;

type LeadFormFieldWithLabelProps = {
  field: LeadFormFieldDescriptor;
  fieldId: string;
  errorMessage?: string;
  isRequired?: boolean;
  theme?: BasicTheme;
  /**
   * Used to pass props from react-hook-form's `register()` call down to the underlying <input> and <select> elements
   */
  inputElementProps: UseFormRegisterReturn;
} & React.HTMLProps<HTMLDivElement>; // We use the `...parentProps` rest parameter to opaquely pass through input props from react-hook-form's `register` call

/**
 * We use an enum of these custom "type" strings (e.g. `dateInput`) in our form builder to describe the type of input
 * Many of them align with the kinds of inputs supported natively by <input> with e.g. type="date" prop
 * This is a mapping of those where relevant -
 * And where `null` means "not implemented using an <input> element"
 * (This, of course, might become redundant if we end up using <Checkbox> elements from a UI library)
 */
const sanityInputTypeToHTMLInputType: Record<FieldTypesThatBecomeInputElements, React.HTMLInputTypeAttribute> = {
  checkbox: 'checkbox',
  dateInput: 'date',
  emailInput: 'email',
  phoneNumberInput: 'tel',
  radiobuttonGroup: 'radio',
  textInput: 'text',
  urlInput: 'url',
};

export const LeadFormFieldWithLabel: React.FC<LeadFormFieldWithLabelProps> = ({
  field,
  fieldId,
  errorMessage,
  isRequired,
  inputElementProps,
  theme,
  ...parentProps
}) => {
  const borderColor = theme === 'darkblue' ? 'border-blue-cornflower' : 'border-dark-gold';
  const labelFontWeight = theme === 'darkblue' ? '' : 'font-bold';
  const [queryParams, setQueryParams] = React.useState<URLSearchParams | undefined>();

  React.useEffect(() => {
    if (typeof window !== undefined) {
      setQueryParams(new URLSearchParams(window.location.search));
    }
  }, []);

  const labelElement = field.fieldLabel ? (
    <label htmlFor={fieldId} className={`${labelFontWeight} block text-xs pb-2`}>
      {field.fieldLabel}
      {isRequired ? <span>*</span> : ''}
    </label>
  ) : null;

  switch (field._type) {
    case 'checkbox': {
      const htmlInputType = sanityInputTypeToHTMLInputType[field._type];

      // Adds style targeting for checkbox fieldsets
      const className = parentProps.className ? parentProps.className : '';
      parentProps.className = `${className} checkbox-fieldset`;

      return (
        <div {...parentProps}>
          {labelElement}
          <input
            type={htmlInputType}
            id={fieldId}
            className={`${borderColor} text-dark-gold`}
            {...inputElementProps}
          ></input>
        </div>
      );
    }
    case 'dateInput': {
      const htmlInputType = sanityInputTypeToHTMLInputType[field._type];
      return (
        <div {...parentProps}>
          {labelElement}
          <input
            type={htmlInputType}
            id={fieldId}
            className={`pl-0 w-full border-0 border-b-1 ${borderColor} bg-transparent`}
            {...inputElementProps}
          ></input>
          <ErrorTextBlurb errorMessage={errorMessage} />
        </div>
      );
    }
    case 'emailInput': {
      const htmlInputType = sanityInputTypeToHTMLInputType[field._type];
      const queryParamEmail = queryParams?.get('nominated_email');
      return (
        <div {...parentProps}>
          {queryParamEmail === null && labelElement}
          <input
            type={htmlInputType}
            id={fieldId}
            value={queryParamEmail ?? undefined}
            readOnly={queryParamEmail != null}
            hidden={queryParamEmail != null}
            className={`pl-0 w-full border-0 border-b-1 ${borderColor} bg-transparent`}
            {...inputElementProps}
          ></input>
          <ErrorTextBlurb errorMessage={errorMessage} />
        </div>
      );
    }
    case 'phoneNumberInput': {
      const htmlInputType = sanityInputTypeToHTMLInputType[field._type];
      return (
        <div {...parentProps}>
          {labelElement}
          <input
            type={htmlInputType}
            id={fieldId}
            className={`pl-0 w-full border-0 border-b-1 ${borderColor} bg-transparent`}
            {...inputElementProps}
          ></input>
          <ErrorTextBlurb errorMessage={errorMessage} />
        </div>
      );
    }
    case 'radiobuttonGroup': {
      const htmlInputType = sanityInputTypeToHTMLInputType[field._type];
      return (
        <div {...parentProps}>
          {labelElement}
          <input type={htmlInputType} id={fieldId} className="" {...inputElementProps}></input>
          <ErrorTextBlurb errorMessage={errorMessage} />
        </div>
      );
    }
    case 'textInput': {
      const htmlInputType = sanityInputTypeToHTMLInputType[field._type];
      return (
        <div {...parentProps}>
          {labelElement}
          <input
            type={htmlInputType}
            id={fieldId}
            className={`pl-0 w-full border-0 border-b-1 ${borderColor} bg-transparent`}
            {...inputElementProps}
          ></input>
          <ErrorTextBlurb errorMessage={errorMessage} />
        </div>
      );
    }
    case 'urlInput': {
      const htmlInputType = sanityInputTypeToHTMLInputType[field._type];
      return (
        <div {...parentProps}>
          {labelElement}
          <input
            type={htmlInputType}
            id={fieldId}
            className={`pl-0 w-full border-0 border-b-1 ${borderColor} bg-transparent`}
            {...inputElementProps}
          ></input>
          <ErrorTextBlurb errorMessage={errorMessage} />
        </div>
      );
    }
    case 'textarea': {
      return (
        <div {...parentProps}>
          {labelElement}
          <textarea
            id={fieldId}
            className={`pl-0 w-full border-1 ${borderColor} bg-transparent`}
            {...inputElementProps}
          ></textarea>
          <ErrorTextBlurb errorMessage={errorMessage} />
        </div>
      );
    }
    case 'dropdown': {
      return (
        <div {...parentProps}>
          {labelElement}
          <select
            id={fieldId}
            className={`pl-0 w-full border-0 border-b-1 ${borderColor} bg-transparent`}
            {...inputElementProps}
          >
            <option disabled selected></option>
            {field.multiselectOptions &&
              field.multiselectOptions.map(optionValue => (
                <option key={`${fieldId}-option-${optionValue}`} value={optionValue}>
                  {optionValue}
                </option>
              ))}
          </select>
          <ErrorTextBlurb errorMessage={errorMessage} />
        </div>
      );
    }
    default: {
      const unsupportedType: never = field;
      // The `never` means that we're type safe in terms of covering all possibilities
      // but we don't have end-to-end input validation, so we might reach this line during SSR sometimes
      // if we don't keep the possible types in sync between sanity and this repo
      throw new Error(`Unsupported field ${JSON.stringify(unsupportedType)}.`);
    }
  }
};
